/**
 * Constants for integrations that support crawler preferences
 * These integrations have additional configuration options for content crawling
 */

/**
 * The tier an integration row belongs to.
 *
 * `user` is a person's own connection. `deployment` is a platform-level account a
 * superadmin connects once; it holds the token and the folder tree but never crawls.
 * `organization` is a workspace configured under a deployment parent — write-only, and
 * only ever read back nested under its parent.
 */
export const INTEGRATION_SCOPES = {
  USER: "user",
  ORGANIZATION: "organization",
  DEPLOYMENT: "deployment",
} as const;

export type IntegrationScope =
  (typeof INTEGRATION_SCOPES)[keyof typeof INTEGRATION_SCOPES];

// Integrations that support crawler preferences
const CRAWLER_PREFERENCE_INTEGRATIONS = [
  "teams",
  "slack",
  "box",
  "onedrive",
  "sharepoint",
  "googledrive",
  "dropbox",
  "onenote",
  "evernote",
  "azure_storage",
  "telegram",
] as const;

// Integrations that support only single account (exceptions)
const SINGLE_ACCOUNT_INTEGRATIONS = [
  "whatsapp",
  "telegram",
  "evernote",
] as const;

// Type for crawler preference integration sources
type CrawlerPreferenceIntegration =
  (typeof CRAWLER_PREFERENCE_INTEGRATIONS)[number];

// Type for single account integration sources
type SingleAccountIntegration = (typeof SINGLE_ACCOUNT_INTEGRATIONS)[number];

// Integrations that use non-OAuth authentication
const NON_OAUTH_INTEGRATIONS = [
  "whatsapp",
  "telegram",
  "azure_storage",
] as const;

// Type for non-OAuth integration sources
type NonOAuthIntegration = (typeof NON_OAUTH_INTEGRATIONS)[number];

/**
 * Integrations that take longer than 20 seconds to fetch crawler preferences
 * These integrations should show a special loading state with messaging
 */
const LONG_LOADING_INTEGRATIONS = ["telegram"] as const;

// Type for long loading integration sources
type LongLoadingIntegration = (typeof LONG_LOADING_INTEGRATIONS)[number];

/**
 * Check if an integration supports crawler preferences
 * @param integration - The integration label to check
 * @returns True if the integration supports crawler preferences
 */
export const hasCrawlerPreferences = (
  integration: string,
): integration is CrawlerPreferenceIntegration => {
  return CRAWLER_PREFERENCE_INTEGRATIONS.includes(
    integration as CrawlerPreferenceIntegration,
  );
};

/**
 * Whether the connect flow includes a folder/channel selection step.
 *
 * Selection is a property of the tier as well as the source: a deployment parent holds
 * the tree but has no crawl of its own, so the API rejects a preference write against it.
 * Those folders are chosen per workspace instead, outside this flow.
 *
 * @param integration - The integration label to check
 * @param integrationScope - The tier being connected
 */
export const selectsCrawlerPreferences = (
  integration: string,
  integrationScope: IntegrationScope = INTEGRATION_SCOPES.USER,
): boolean =>
  integrationScope !== INTEGRATION_SCOPES.DEPLOYMENT &&
  hasCrawlerPreferences(integration);

/**
 * Check if an integration supports multiple accounts
 * @param integration - The integration label to check
 * @returns True if the integration supports multiple accounts
 */
export const supportsMultipleAccounts = (integration: string): boolean => {
  return !SINGLE_ACCOUNT_INTEGRATIONS.includes(
    integration as SingleAccountIntegration,
  );
};

/**
 * Check if an integration uses non-OAuth authentication
 * @param integration - The integration label to check
 * @returns True if the integration uses non-OAuth authentication
 */
export const isNonOAuthIntegration = (
  integration: string,
): integration is NonOAuthIntegration => {
  return NON_OAUTH_INTEGRATIONS.includes(integration as NonOAuthIntegration);
};

/**
 * Check if an integration takes longer than 20 seconds to fetch crawler preferences
 * @param integration - The integration label to check
 * @returns True if the integration is a long-loading integration
 */
export const isLongLoadingIntegration = (
  integration: string,
): integration is LongLoadingIntegration => {
  return LONG_LOADING_INTEGRATIONS.includes(
    integration as LongLoadingIntegration,
  );
};
